import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Prose } from '@/components/prose';
import { Badge, buttonClass, inputClass, textareaClass } from '@/components/ui';
import { ReadingRecord } from '@/components/admin/reading-record';
import { documentsQuery } from '@/queries';
import type { DocCategory, DocumentRow, DocumentVersion } from '@/lib/stock-types';

/**
 * Procedures, guidelines and safety. Spec 003 §8, User Story 7.
 *
 * The client's words: the guidelines and procedures, including health and
 * safety, in the application so staff can get at them **on demand** and see how
 * to do things properly.
 *
 * So the screen is built for somebody standing at a machine with a question,
 * not for an auditor: what you have not read is at the top, the categories are
 * named in plain words, and the text opens in place rather than downloading.
 *
 * **Signing attaches to the version you read.** Publish a new one and everybody
 * owes it again, and the old signature stays where it was. The screen says
 * that, because a person who signs something expects it to mean something.
 */

const CATEGORY_LABEL: Record<DocCategory, string> = {
  procedure: 'Procedures',
  work_instruction: 'How to do the job',
  safety_policy: 'Health and safety',
  swms: 'Safe work method statements',
  sds: 'Safety data sheets',
  induction: 'Induction',
  training: 'Training',
  other: 'Other',
};

async function call(path: string, init?: RequestInit) {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : await res.json();
}

function Reader({ doc }: { doc: DocumentRow }) {
  const qc = useQueryClient();
  const [data, setData] = useState<{
    current: DocumentVersion | null;
    acknowledged_at: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useMutation({
    mutationFn: () => call(`/api/documents/${doc.id}`),
    onSuccess: (d) => setData(d as never),
    onError: (e: Error) => setError(e.message),
  });

  const sign = useMutation({
    mutationFn: (versionId: string) =>
      call(`/api/documents/${doc.id}/acknowledge`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ version_id: versionId }),
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
      load.mutate();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (data === null) {
    return (
      <div className="border-t border-line p-4">
        <button
          type="button"
          onClick={() => load.mutate()}
          className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold"
        >
          {load.isPending ? 'Opening…' : 'Read it'}
        </button>
        {error && <p className="mt-3 text-[14px]">{error}</p>}
      </div>
    );
  }

  return (
    <div className="border-t border-line p-4">
      {error && (
        <p className="mb-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>
      )}

      {!data.current ? (
        <p className="text-[14px] text-muted">
          Nothing has been published for this yet. A draft is not something to
          work from, so it is not shown here.
        </p>
      ) : (
        <>
          {/* A procedure somebody reads at a machine cannot look like source
              code. It was rendered with `whitespace-pre-wrap`, so every `#` and
              every `**` was on the screen. R-194. */}
          {data.current.body
            ? <Prose source={data.current.body} />
            : <p className="text-[14px] text-muted">This one is an attached file.</p>}

          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4">
            {data.acknowledged_at ? (
              <span className="text-[14px] text-muted">
                You read version {data.current.version} on{' '}
                {new Date(data.acknowledged_at).toLocaleDateString('en-AU')}.
              </span>
            ) : (
              <>
                <button
                  type="button"
                  disabled={sign.isPending}
                  onClick={() => sign.mutate(data.current!.id)}
                  className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
                >
                  I have read this
                </button>
                <span className="text-[13px] text-muted">
                  Recorded against version {data.current.version}. If this is
                  updated later you will be asked again, and today's record stays
                  as it is.
                </span>
              </>
            )}
          </div>

          <Attachment
            doc={doc}
            version={data.current}
            onChanged={() => load.mutate()}
          />

          <Corrector doc={doc} onSaved={() => load.mutate()} />
        </>
      )}
    </div>
  );
}

/**
 * The file attached to a procedure. FR-711.
 *
 * The client's ask, in their words: staff need the manuals for the machines
 * they bought, in the same place as the procedures. Those arrive as PDFs.
 *
 * **The file belongs to a VERSION.** That is the same rule the rest of this
 * module follows: a signature attaches to the version somebody read, so
 * replacing a file in place would silently change what every existing
 * signature refers to. A new revision of a manual is a correction, and the
 * correction carries the new file.
 */
function Attachment({ doc, version, onChanged }: {
  doc: DocumentRow;
  version: DocumentVersion | null;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.set('file', file);
      const res = await fetch(`/api/documents/${doc.id}/file`, {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not attach it.');
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (version?.file_path) {
    return (
      <div className="border-t border-line px-4 py-3">
        <a
          href={`/api/document-versions/${version.id}/file`}
          target="_blank"
          rel="noreferrer"
          className={buttonClass('secondary', 'sm')}
        >
          Open the attached file
        </a>
        <span className="ml-3 text-[12.5px] text-muted">
          Attached to version {version.version}. A newer revision goes on a correction.
        </span>
      </div>
    );
  }

  return (
    <div className="border-t border-line px-4 py-3">
      <label className={buttonClass('secondary', 'sm', 'cursor-pointer')}>
        {busy ? 'Attaching...' : 'Attach a file'}
        <input
          type="file"
          className="sr-only"
          disabled={busy}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            // Clear it, so choosing the same file twice still fires.
            e.target.value = '';
          }}
        />
      </label>
      <span className="ml-3 text-[12.5px] text-muted">
        A machine manual, a safety data sheet or a scanned form. PDF, Word,
        Excel, text or an image, up to 25 MB.
      </span>
      {error && (
        <p className="mt-2 rounded-[10px] border border-line-strong p-3 text-[13px]">{error}</p>
      )}
    </div>
  );
}

/**
 * Correct a procedure by publishing the next version.
 *
 * A version is never edited in place. The acknowledgement is attached to the
 * version that was read, so changing the words under a signature turns "Ana
 * read the guarding procedure" into a claim about a document Ana never saw.
 * A correction is version n+1, everybody owes it again, and the old signatures
 * stay against the old text. The screen has always said that; this is the
 * control that makes it true.
 */
function Corrector({ doc, onSaved }: { doc: DocumentRow; onSaved: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => call(`/api/documents/${doc.id}/versions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text, change_note: note, publish: true }),
    }),
    onSuccess: () => {
      setError(null); setText(''); setNote(''); setOpen(false);
      void qc.invalidateQueries({ queryKey: ['documents'] });
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <div className="border-t border-line px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={buttonClass('secondary', 'sm')}
        >
          Correct this
        </button>
      </div>
    );
  }

  return (
    <form
      className="border-t border-line px-4 py-3"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <p className="mb-2 text-[13px] text-muted">
        This publishes a new version. Everybody is asked to read it again, and
        the signatures on the old one stay where they are.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        placeholder="The whole procedure, as it should now read."
        className={`${textareaClass} w-full resize-y font-[family-name:var(--font-mono)] text-[13px]`}
        required
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What changed, in a line"
        className={`${inputClass} mt-2 w-full`}
      />
      {error && (
        <p className="mt-2 rounded-[10px] border border-line p-3 text-[13px]">{error}</p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={save.isPending} className={buttonClass('primary', 'sm')}>
          {save.isPending ? 'Publishing...' : 'Publish the new version'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className={buttonClass('secondary', 'sm')}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Write a procedure, in the application.
 *
 * Until now there was no way to add one. Every document in the database came
 * from `scripts/demo-procedures.ts`, run by a developer from a terminal, so the
 * client could read and sign procedures and could not write one. The schema
 * always supported it; nothing was wired to it.
 *
 * **Publish is a deliberate second act.** A procedure that goes live the
 * instant somebody starts typing is a procedure the floor is asked to sign
 * halfway through being written, so the default is a draft and publishing is a
 * tick.
 *
 * The text is plain text with headings and numbered steps, rendered by the
 * same `Prose` the reader uses, so what is typed here is what the floor sees.
 */
function Writer() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<DocCategory>('procedure');
  const [text, setText] = useState('');
  const [publish, setPublish] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => call('/api/documents', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title, category, text, publish }),
    }),
    onSuccess: () => {
      setError(null);
      setTitle(''); setText(''); setPublish(false); setOpen(false);
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClass('primary', 'md', 'mb-6')}
      >
        Write a procedure
      </button>
    );
  }

  return (
    <form
      className="mb-6 rounded-[10px] border border-line-strong bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_260px]">
        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="How to change a screen"
            className={`${inputClass} w-full`}
            required
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Kind</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as DocCategory)}
            className={`${inputClass} w-full`}
          >
            {(Object.keys(CATEGORY_LABEL) as DocCategory[]).map((k) => (
              <option key={k} value={k}>{CATEGORY_LABEL[k]}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-[12.5px] font-semibold">The procedure</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          placeholder={'## Before you start\n\nSwitch the machine off at the wall.\n\n1. Wait for the head to stop.\n2. Put on the gloves.'}
          className={`${textareaClass} w-full resize-y font-[family-name:var(--font-mono)] text-[13px]`}
          required
        />
        {/*
          * The hint has to describe what the RENDERER does, not what a
          * reasonable person might assume. "Headings on their own line" was
          * wrong: the parser joins consecutive lines into one paragraph, so a
          * heading with text under it came out as "Before you start Put on the
          * gloves." on the floor's screen. The hint is the only place a writer
          * finds out, so it names the four marks that work.
          */}
        <span className="mt-1 block text-[12px] text-muted">
          A heading starts with <code className="font-[family-name:var(--font-mono)]">##</code>.
          Steps start with <code className="font-[family-name:var(--font-mono)]">1.</code>,
          points with <code className="font-[family-name:var(--font-mono)]">-</code>, and
          <code className="font-[family-name:var(--font-mono)]">**two asterisks**</code> make
          a safety line bold. Leave a blank line between paragraphs. It is shown
          to the floor exactly as it reads here.
        </span>
      </label>

      <label className="mt-3 flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
        />
        Publish it now, and ask everybody to read it
      </label>

      {error && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[13px]">{error}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={create.isPending} className={buttonClass('primary', 'sm')}>
          {create.isPending ? 'Saving...' : publish ? 'Save and publish' : 'Save as a draft'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className={buttonClass('secondary', 'sm')}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminDocumentsPage() {
  const { documents } = useSuspenseQuery(documentsQuery).data;
  const [openId, setOpenId] = useState<string | null>(null);

  const published = documents.filter((d) => d.state === 'published');
  const owed = published.filter((d) => d.outstanding);
  const rest = published.filter((d) => !d.outstanding);

  const groups = new Map<string, DocumentRow[]>();
  for (const d of rest) groups.set(d.category, [...(groups.get(d.category) ?? []), d]);

  const item = (d: DocumentRow) => (
    <div key={d.id}>
      <button
        type="button"
        onClick={() => setOpenId(openId === d.id ? null : d.id)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="font-medium">{d.title}</span>
        {d.outstanding && <Badge tone="warn">not read yet</Badge>}
      </button>
      {openId === d.id && <Reader doc={d} />}
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Procedures</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Procedures, health and safety, and how to run each job properly.
        </p>
      </div>

      <Writer />

      <div className="mb-6">
        <ReadingRecord />
      </div>

      {published.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">Nothing has been published yet.</p>
          <p className="mx-auto mt-2 max-w-[54ch] text-[14px] text-muted">
            This is where the procedures, the safe work method statements and the
            safety data sheets live, so anybody can check how something is done
            without finding the folder. Use "Write a procedure" above. They go in
            once and are then kept current here.
          </p>
        </div>
      ) : (
        <>
          {owed.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
                To read ({owed.length})
              </h2>
              <div className="rounded-[10px] border border-line-strong bg-panel">
                {owed.map(item)}
              </div>
            </section>
          )}

          {[...groups.entries()].map(([category, docs]) => (
            <section key={category} className="mb-8">
              <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
                {CATEGORY_LABEL[category as DocCategory] ?? category}
              </h2>
              <div className="rounded-[10px] border border-line bg-panel">
                {docs.map(item)}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
